import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { RETIRED_BOARD_COLORS, RETIRED_PIECE_COLORS } from '../utils/retired-themes';
import {
  DEFAULT_PREFERENCES,
  APPEARANCE_KEYS,
  PIECE_SHAPES,
  PIECE_STYLES,
  CROWN_TYPES,
  KING_ICONS,
  MATERIALS,
  PIECE_COLOR_THEMES,
  BOARD_THEMES,
  BOARD_NOTATIONS,
  PREFERRED_CADENCES,
  UI_THEMES,
  CUSTOM_COLOR_KEYS,
  isFlatStyle,
  isHexColor,
  type UserPreferences,
  type PieceShape,
  type PieceStyle,
  type KingIcon,
  type CrownType,
  type Material,
  type PieceColorTheme,
  type BoardTheme,
  type BoardNotation,
  type PreferredCadence,
  type UiTheme,
} from '@draughts/shared';
import { api } from '../services/api';
import { useAuthStore } from './auth-store';
import { log } from '../utils/logger';
import {
  trackPreferredVariantChanged,
  trackCadenceChanged,
  trackDailyGoalChanged,
  trackBoardThemeChanged,
  trackBoardMaterialChanged,
  trackBoardNotationChanged,
  trackPieceColorThemeChanged,
  trackPieceMaterialChanged,
  trackPieceShapeChanged,
  trackPieceStyleChanged,
  trackCrownTypeChanged,
  trackKingIconChanged,
  trackSoundToggled,
  trackVibrationToggled,
  trackMoveHintsToggled,
  trackPremoveToggled,
  trackUiThemeChanged,
} from '../utils/analytics';

const LEGACY_AUDIO_KEY = 'draughts-audio-enabled';

interface PreferencesStore extends UserPreferences {
  setPieceShape: (shape: PieceShape) => void;
  setPieceStyle: (style: PieceStyle) => void;
  setKingIcon: (icon: KingIcon) => void;
  setCrownType: (type: CrownType) => void;
  setPieceColorTheme: (theme: PieceColorTheme) => void;
  setCustomColor: (key: (typeof CUSTOM_COLOR_KEYS)[number], hex: string) => void;
  /** Conferma la coppia scelta nel selettore: tema 'custom' + i due esadecimali, in un colpo solo. */
  applyCustomColors: (axis: 'piece' | 'board', light: string, dark: string) => void;
  setPieceMaterial: (material: Material) => void;
  setBoardTheme: (theme: BoardTheme) => void;
  setBoardMaterial: (material: Material) => void;
  setBoardNotation: (notation: BoardNotation) => void;
  setMoveSoundEnabled: (enabled: boolean) => void;
  setLowTimeSoundEnabled: (enabled: boolean) => void;
  setNotificationSoundEnabled: (enabled: boolean) => void;
  setVibrationEnabled: (enabled: boolean) => void;
  setShowMoveHints: (enabled: boolean) => void;
  setShowLastMove: (enabled: boolean) => void;
  setShowLastMoveCaptures: (enabled: boolean) => void;
  setPreferredVariant: (variant: string) => void;
  setPreferredCadence: (cadence: PreferredCadence) => void;
  setDailyGoalTarget: (target: number) => void;
  setPremoveEnabled: (enabled: boolean) => void;
  setUiTheme: (theme: UiTheme) => void;
  hydrateFromServer: () => Promise<void>;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSync(prefs: UserPreferences): void {
  if (!useAuthStore.getState().isAuthenticated) { log.info('Preferences', 'scheduleSync skipped (not authenticated)'); return; }
  if (useAuthStore.getState().isGuest) { log.info('Preferences', 'scheduleSync skipped (guest)'); return; }
  if (syncTimer) { log.info('Preferences', 'scheduleSync debounce cancel (pending timer replaced)'); clearTimeout(syncTimer); }
  syncTimer = setTimeout(() => {
    log.info('Preferences', 'updatePreferences PATCH firing', { keys: Object.keys(prefs) });
    api.updatePreferences(prefs)
      .then((saved) => {
        log.info('Preferences', 'updatePreferences saved (server echo)', { keys: Object.keys(saved) });
      })
      .catch((err: unknown) => {
        log.warn('Preferences', 'updatePreferences failed', { error: err instanceof Error ? err.message : String(err) });
      });
  }, 500);
}

/**
 * Riporta ogni valore enum dentro il dominio conosciuto da QUESTA build.
 *
 * Serve perché `extract()` invia l'oggetto preferenze COMPLETO a ogni setter e lo
 * schema server è `.strict()` con enum: un solo valore fuori dominio — scritto da
 * una build più recente e rimasto in localStorage, oppure sopravvissuto in
 * `users.preferences` dopo un rollback di @draughts/shared — farebbe fallire con
 * 400 *tutti* i salvataggi di preferenze (audio, tema, premove, cadenza), con
 * l'errore ingoiato dal `.catch()` di scheduleSync. Va applicato sia alla
 * reidratazione da localStorage sia alla risposta del server.
 */
function clampEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * Converte una palette ritirata in 'custom' con i suoi stessi colori.
 *
 * DEVE girare prima di `clampEnum`: quella riporta al default qualunque valore fuori
 * dominio, e dopo di lei 'ocean' è già diventato 'auto' — l'informazione da cui dipende
 * tutta la conversione non esiste più. La validazione degli esadecimali sta invece DOPO,
 * così i colori appena scritti passano gli stessi controlli di tutti gli altri.
 *
 * Sovrascrive un'eventuale coppia personalizzata salvata ma non in uso: chi aveva scelto
 * 'ocean' non stava guardando i propri `customBoard*`, e non c'è modo di conservare
 * entrambe le cose senza aggiungere una chiave. Vince quello che l'utente vedeva.
 */
function convertRetiredThemes(prefs: Partial<UserPreferences>): void {
  const piece = prefs.pieceColorTheme && RETIRED_PIECE_COLORS[prefs.pieceColorTheme];
  if (piece) {
    prefs.pieceColorTheme = 'custom';
    prefs.customPieceLight = piece.light;
    prefs.customPieceDark = piece.dark;
  }
  const board = prefs.boardTheme && RETIRED_BOARD_COLORS[prefs.boardTheme];
  if (board) {
    prefs.boardTheme = 'custom';
    prefs.customBoardLight = board.light;
    prefs.customBoardDark = board.dark;
  }
}

/**
 * Il tipo di corona di chi l'aveva scelto PRIMA che diventasse una preferenza sua.
 *
 * Per un po' la corona e l'emblema della dama hanno condiviso `kingIcon`, e chi giocava con
 * lo stile a corona ha scelto lì il suo tipo: senza questa eredità se lo vedrebbe azzerato
 * sulla Classica al primo avvio dopo l'aggiornamento.
 *
 * Sta in `clampPreferences` e non nella migrazione della persistenza perché gli ingressi sono
 * DUE — `onRehydrateStorage` per localStorage, `hydrateFromServer` per il server — e passano
 * entrambi da qui con l'oggetto intero. È idempotente: appena `crownType` esiste non si deriva
 * più, e su una patch parziale (senza stile o senza icona) la guardia non scatta.
 *
 * Va dopo la conversione `gem → diamond` di sopra: chi aveva scelto la gemma prima della
 * rinomina erediterebbe altrimenti un valore che non esiste più.
 */
const CROWN_TYPE_FROM_KING_ICON: Record<KingIcon, CrownType> = {
  crown: 'classic',
  none: 'plain',
  diamond: 'diadem',
  star: 'lily',
  ring: 'imperial',
};

function inheritCrownType<T extends Partial<UserPreferences>>(prefs: T): void {
  if (prefs.crownType !== undefined) return;
  if (prefs.pieceStyle !== '3d-crown' || prefs.kingIcon === undefined) return;
  prefs.crownType = CROWN_TYPE_FROM_KING_ICON[prefs.kingIcon];
}

function clampPreferences<T extends Partial<UserPreferences>>(prefs: T): T {
  const d = DEFAULT_PREFERENCES;
  convertRetiredThemes(prefs);
  if (prefs.pieceShape !== undefined) prefs.pieceShape = clampEnum(prefs.pieceShape, PIECE_SHAPES, d.pieceShape);
  if (prefs.pieceStyle !== undefined) prefs.pieceStyle = clampEnum(prefs.pieceStyle, PIECE_STYLES, d.pieceStyle);
  // 'gem' è stato rinominato 'diamond': va convertito PRIMA del clamp, che altrimenti
  // riporta al default (nessuna icona) chi aveva scelto proprio quell'emblema. Stessa
  // regola d'ordine di `convertRetiredThemes`, e stessi due ingressi: localStorage e
  // risposta del server, dove il vecchio id resta scritto finché non si tocca nulla.
  if ((prefs.kingIcon as string) === 'gem') prefs.kingIcon = 'diamond';
  if (prefs.kingIcon !== undefined) prefs.kingIcon = clampEnum(prefs.kingIcon, KING_ICONS, d.kingIcon);
  inheritCrownType(prefs);
  // 'jewelled' è stata sostituita dal diadema: come per `gem → diamond`, la conversione va
  // PRIMA del clamp, che altrimenti riporta al default chi aveva scelto proprio quel tipo.
  if ((prefs.crownType as string) === 'jewelled') prefs.crownType = 'diadem';
  if (prefs.crownType !== undefined) prefs.crownType = clampEnum(prefs.crownType, CROWN_TYPES, d.crownType);
  if (prefs.pieceColorTheme !== undefined) prefs.pieceColorTheme = clampEnum(prefs.pieceColorTheme, PIECE_COLOR_THEMES, d.pieceColorTheme);
  if (prefs.pieceMaterial !== undefined) prefs.pieceMaterial = clampEnum(prefs.pieceMaterial, MATERIALS, d.pieceMaterial);
  if (prefs.boardTheme !== undefined) prefs.boardTheme = clampEnum(prefs.boardTheme, BOARD_THEMES, d.boardTheme);
  if (prefs.boardMaterial !== undefined) prefs.boardMaterial = clampEnum(prefs.boardMaterial, MATERIALS, d.boardMaterial);
  if (prefs.boardNotation !== undefined) prefs.boardNotation = clampEnum(prefs.boardNotation, BOARD_NOTATIONS, d.boardNotation);
  if (prefs.preferredCadence !== undefined) prefs.preferredCadence = clampEnum(prefs.preferredCadence, PREFERRED_CADENCES, d.preferredCadence);
  if (prefs.uiTheme !== undefined) prefs.uiTheme = clampEnum(prefs.uiTheme, UI_THEMES, d.uiTheme);
  // I colori personalizzati sono stringhe libere, non enum: un valore non esadecimale
  // non farebbe fallire nulla in modo visibile, produrrebbe gradienti sbagliati
  // (`lighten`/`darken` leggono le cifre con parseInt e restituiscono NaN).
  //
  // Si validano e basta. Il selettore lascia scegliere QUALUNQUE colore, quindi qui non
  // c'è più niente da normalizzare: la proiezione che riportava ogni valore su una
  // chiarezza fissa è stata tolta apposta, perché rimetterebbe il colore appena scelto
  // dove piace a lei a ogni avvio e a ogni sincronizzazione dal server.
  for (const key of CUSTOM_COLOR_KEYS) {
    if (prefs[key] === undefined) continue;
    if (!isHexColor(prefs[key])) prefs[key] = d[key];
  }
  return prefs;
}

/**
 * v1 — riorganizzazione delle impostazioni grafiche da 5 assi a 7 (azzera l'ASPETTO).
 * v2 — l'unico `soundEnabled` si separa in tre canali indipendenti.
 * v3 — il tipo di corona esce da `kingIcon` e diventa `crownType`.
 *
 * Ogni versione è un PASSO a sé in `migratePersisted`, non una soglia: vedi il commento
 * lì sotto per il guasto che l'alternativa produce.
 */
const PREFERENCES_VERSION = 3;

const PREFERENCES_KEY = 'draughts-preferences';

/**
 * Marca con `version: 0` i blob salvati PRIMA che questo store dichiarasse una versione.
 *
 * Senza questo passaggio il reset dell'aspetto non raggiungerebbe nessuno degli utenti
 * a cui è destinato. zustand (v5) chiama `migrate` solo dentro
 *
 *     if (typeof deserializedStorageValue.version === "number" && ... !== options.version)
 *
 * e i blob già in circolazione sono stati scritti da un `persist` dichiarato senza
 * `version`, quindi quel campo lì non c'è proprio: non è `0`, è `undefined`, e il
 * `typeof` lo scarta prima ancora del confronto. Il risultato sarebbe un `migrate`
 * scritto, testato e mai eseguito — e un test che parte da storage vuoto non se ne
 * accorgerebbe, perché senza blob `migrate` non viene invocata comunque.
 *
 * Gira al caricamento del modulo, PRIMA di `create()`: quando zustand legge lo storage
 * il campo c'è già.
 */
function stampUnversionedBlob(): void {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { state?: unknown; version?: unknown };
    if (typeof parsed?.version === 'number') return;
    // Un blob senza `state` non è nel formato di zustand: lo si tratta come lo stato nudo.
    const state = parsed && typeof parsed === 'object' && 'state' in parsed ? parsed.state : parsed;
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify({ state, version: 0 }));
  } catch {
    // Storage inaccessibile o JSON corrotto: zustand ripartirà dai default, che è
    // esattamente l'esito che questo reset vuole ottenere.
  }
}

stampUnversionedBlob();

/**
 * Porta un blob salvato fino alla versione corrente, un PASSO alla volta.
 *
 * I passi sono cumulativi e ognuno è condizionato alla propria versione, non a
 * `version < PREFERENCES_VERSION`. Con una soglia unica, alzare la costante rieseguirebbe
 * anche i passi già fatti: alla v2 ogni utente già migrato si vedrebbe azzerare l'aspetto
 * una SECONDA volta, perdendo forma, colori e materiali scelti nel frattempo.
 */
function migratePersisted(persisted: unknown, version: number): unknown {
  const p = { ...(persisted as Record<string, unknown>) };

  // v1: le impostazioni grafiche passano da 5 assi a 7 e i vecchi domini non esistono
  // più — reset per tutti, non mappatura conservativa.
  if (version < 1) {
    for (const key of APPEARANCE_KEYS) p[key] = DEFAULT_PREFERENCES[key];
  }

  // v2: `soundEnabled` era l'UNICO interruttore audio. Chi l'aveva spento voleva silenzio
  // su tutti e tre i canali: lasciandolo ereditare il default `true` si ritroverebbe
  // l'allarme del tempo e il suono di partita trovata senza aver toccato niente.
  if (version < 2) {
    const legacy = p.soundEnabled;
    if (typeof legacy === 'boolean') {
      p.moveSoundEnabled ??= legacy;
      p.lowTimeSoundEnabled ??= legacy;
      p.notificationSoundEnabled ??= legacy;
    }
    delete p.soundEnabled;
  }

  // v3: la corona e l'emblema della dama hanno condiviso `kingIcon` per un po'. Chi giocava
  // con lo stile a corona ha scelto lì il suo tipo, e senza questo passo se lo vedrebbe
  // azzerato sulla Classica al primo avvio dopo l'aggiornamento.
  //
  // Qui e non in `clampPreferences`: quella riceve lo stato GIÀ FUSO con i default, dove
  // `crownType` non è più assente ma vale 'classic', e la guardia non scatterebbe mai. Sul
  // percorso del SERVER invece l'oggetto arriva senza la chiave, e lì l'eredità sta in
  // `inheritCrownType`. Due ingressi, due punti: verificato in browser, non dedotto.
  if (version < 3) {
    if (p.crownType === undefined && p.pieceStyle === '3d-crown' && typeof p.kingIcon === 'string') {
      p.crownType = CROWN_TYPE_FROM_KING_ICON[p.kingIcon as KingIcon];
    }
  }

  return p;
}

function readLegacyAudioFlag(): boolean | null {
  try {
    const v = localStorage.getItem(LEGACY_AUDIO_KEY);
    if (v === null) return null;
    localStorage.removeItem(LEGACY_AUDIO_KEY);
    return v !== 'false';
  } catch {
    return null;
  }
}

export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_PREFERENCES,

      setPieceShape: (pieceShape) => {
        set({ pieceShape });
        scheduleSync(extract(get()));
        trackPieceShapeChanged(pieceShape);
      },
      setPieceStyle: (pieceStyle) => {
        // Su uno stile piatto la dama non ha volume, quindi senza emblema sarebbe
        // identica a una pedina: `none` va riportata a 'star', la prima icona che la
        // lista offre davvero. Sta qui e non nel pannello perché valga anche per la
        // modale in partita, e con un solo sync.
        const dropNoIcon = isFlatStyle(pieceStyle) && get().kingIcon === 'none';
        set(dropNoIcon ? { pieceStyle, kingIcon: 'star' } : { pieceStyle });
        scheduleSync(extract(get()));
        trackPieceStyleChanged(pieceStyle);
        if (dropNoIcon) trackKingIconChanged('star');
      },
      setKingIcon: (kingIcon) => {
        set({ kingIcon });
        scheduleSync(extract(get()));
        trackKingIconChanged(kingIcon);
      },
      setCrownType: (crownType) => {
        set({ crownType });
        scheduleSync(extract(get()));
        trackCrownTypeChanged(crownType);
      },
      setPieceColorTheme: (pieceColorTheme) => {
        set({ pieceColorTheme });
        scheduleSync(extract(get()));
        trackPieceColorThemeChanged(pieceColorTheme);
      },
      setCustomColor: (key, hex) => {
        if (!isHexColor(hex)) return;
        set({ [key]: hex } as Partial<UserPreferences>);
        scheduleSync(extract(get()));
        // Niente evento: la tinta esatta scelta da un utente non è un dato di prodotto —
        // conta che abbia scelto 'custom', che `piece-color-theme-changed` registra già.
      },
      /**
       * Un solo `set` e non tre setter in fila: ogni `set` fa ridisegnare tutte le
       * scacchiere a video, e confermare il selettore cambia tre valori insieme. La
       * sincronizzazione col server sarebbe comunque una sola (il debounce le fonde), i
       * render no.
       */
      applyCustomColors: (axis, light, dark) => {
        if (!isHexColor(light) || !isHexColor(dark)) return;
        const patch = (axis === 'piece'
          ? { pieceColorTheme: 'custom', customPieceLight: light, customPieceDark: dark }
          : { boardTheme: 'custom', customBoardLight: light, customBoardDark: dark }
        ) as Partial<UserPreferences>;
        set(patch);
        scheduleSync(extract(get()));
        if (axis === 'piece') trackPieceColorThemeChanged('custom');
        else trackBoardThemeChanged('custom');
      },
      setPieceMaterial: (pieceMaterial) => {
        set({ pieceMaterial });
        scheduleSync(extract(get()));
        trackPieceMaterialChanged(pieceMaterial);
      },
      setBoardTheme: (boardTheme) => {
        set({ boardTheme });
        scheduleSync(extract(get()));
        trackBoardThemeChanged(boardTheme);
      },
      setBoardMaterial: (boardMaterial) => {
        set({ boardMaterial });
        scheduleSync(extract(get()));
        trackBoardMaterialChanged(boardMaterial);
      },

      setBoardNotation: (boardNotation) => {
        set({ boardNotation });
        scheduleSync(extract(get()));
        trackBoardNotationChanged(boardNotation);
      },
      setMoveSoundEnabled: (moveSoundEnabled) => {
        set({ moveSoundEnabled });
        scheduleSync(extract(get()));
        trackSoundToggled('move', moveSoundEnabled);
      },
      setLowTimeSoundEnabled: (lowTimeSoundEnabled) => {
        set({ lowTimeSoundEnabled });
        scheduleSync(extract(get()));
        trackSoundToggled('low-time', lowTimeSoundEnabled);
      },
      setNotificationSoundEnabled: (notificationSoundEnabled) => {
        set({ notificationSoundEnabled });
        scheduleSync(extract(get()));
        trackSoundToggled('notification', notificationSoundEnabled);
      },
      setVibrationEnabled: (vibrationEnabled) => {
        set({ vibrationEnabled });
        scheduleSync(extract(get()));
        trackVibrationToggled(vibrationEnabled);
      },
      setShowMoveHints: (showMoveHints) => {
        set({ showMoveHints });
        scheduleSync(extract(get()));
        trackMoveHintsToggled(showMoveHints);
      },
      setShowLastMove: (showLastMove) => {
        set({ showLastMove });
        scheduleSync(extract(get()));
      },
      setShowLastMoveCaptures: (showLastMoveCaptures) => {
        set({ showLastMoveCaptures });
        scheduleSync(extract(get()));
      },
      setPreferredVariant: (preferredVariant) => {
        set({ preferredVariant });
        scheduleSync(extract(get()));
        trackPreferredVariantChanged(preferredVariant);
      },
      setPreferredCadence: (preferredCadence) => {
        set({ preferredCadence });
        scheduleSync(extract(get()));
        trackCadenceChanged(preferredCadence);
      },
      setDailyGoalTarget: (dailyGoalTarget) => {
        set({ dailyGoalTarget });
        scheduleSync(extract(get()));
        trackDailyGoalChanged(dailyGoalTarget);
      },
      setPremoveEnabled: (premoveEnabled) => {
        set({ premoveEnabled });
        scheduleSync(extract(get()));
        trackPremoveToggled(premoveEnabled);
      },

      setUiTheme: (uiTheme) => {
        set({ uiTheme });
        scheduleSync(extract(get()));
        trackUiThemeChanged(uiTheme);
      },

      hydrateFromServer: async () => {
        // Wait until auth has actually been established (token ready), not just the persisted
        // isAuthenticated flag — otherwise this fires before checkAuth/refresh and 401s.
        if (!useAuthStore.getState().isAuthReady) return;
        if (!useAuthStore.getState().isAuthenticated) return;
        if (useAuthStore.getState().isGuest) return;
        try {
          const prefs = await api.getPreferences();
          log.info('Preferences', 'hydrated from server (may clobber local edits)', { keys: Object.keys(prefs) });
          set(clampPreferences({ ...prefs }));
        } catch (err: unknown) {
          log.warn('Preferences', 'getPreferences failed', { error: err instanceof Error ? err.message : String(err) });
        }
      },
    }),
    {
      name: PREFERENCES_KEY,
      version: PREFERENCES_VERSION,
      migrate: migratePersisted,
      partialize: (state) => extract(state),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        clampPreferences(state);
        const legacy = readLegacyAudioFlag();
        if (legacy !== null) {
          state.moveSoundEnabled = legacy;
          state.lowTimeSoundEnabled = legacy;
          state.notificationSoundEnabled = legacy;
        }
      },
    },
  ),
);

function extract(state: UserPreferences): UserPreferences {
  return {
    pieceShape: state.pieceShape,
    pieceStyle: state.pieceStyle,
    kingIcon: state.kingIcon,
    crownType: state.crownType,
    pieceColorTheme: state.pieceColorTheme,
    customPieceLight: state.customPieceLight,
    customPieceDark: state.customPieceDark,
    pieceMaterial: state.pieceMaterial,
    boardTheme: state.boardTheme,
    customBoardLight: state.customBoardLight,
    customBoardDark: state.customBoardDark,
    boardMaterial: state.boardMaterial,
    boardNotation: state.boardNotation,
    moveSoundEnabled: state.moveSoundEnabled,
    lowTimeSoundEnabled: state.lowTimeSoundEnabled,
    notificationSoundEnabled: state.notificationSoundEnabled,
    vibrationEnabled: state.vibrationEnabled,
    showMoveHints: state.showMoveHints,
    showLastMove: state.showLastMove,
    showLastMoveCaptures: state.showLastMoveCaptures,
    preferredVariant: state.preferredVariant,
    preferredCadence: state.preferredCadence,
    dailyGoalTarget: state.dailyGoalTarget,
    premoveEnabled: state.premoveEnabled,
    uiTheme: state.uiTheme,
  };
}
